import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SummaryCards } from "../components/SummaryCards";
import { OpportunityCard } from "../components/OpportunityCard";
import { OpportunityHistoryDialog } from "../components/OpportunityHistoryDialog";
import { QuickUpdateDialog } from "../components/QuickUpdateDialog";
import { useAuth } from "../lib/auth";
import { isDemoMode } from "../lib/demo-mode";
import { getOpportunities, getSummary, updateOpportunity } from "../lib/api";
import { useI18n } from "../lib/i18n";
import type {
  DashboardSummary,
  Opportunity,
  OpportunityUpdateInput,
} from "../types";

export function DashboardPage() {
  const { session } = useAuth();
  const { t } = useI18n();
  const [summary, setSummary] = useState<DashboardSummary>({
    totalActive: 0,
    overdue: 0,
    dueThisWeek: 0,
    confirmed: 0,
  });
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [selected, setSelected] = useState<Opportunity | null>(null);
  const [historySelected, setHistorySelected] = useState<Opportunity | null>(
    null,
  );
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!session) return;
    void Promise.all([
      getSummary(session.accessToken),
      getOpportunities(session.accessToken),
    ]).then(([nextSummary, items]) => {
      setSummary(nextSummary);
      setOpportunities(items);
    });
  }, [session]);

  async function save(input: OpportunityUpdateInput) {
    if (!session || !selected) return;
    const updated = await updateOpportunity(
      session.accessToken,
      selected.id,
      input,
    );
    setOpportunities((items) =>
      items.map((item) => (item.id === updated.id ? updated : item)),
    );
    setToast(t("saved"));
    window.setTimeout(() => setToast(""), 2500);
  }

  return (
    <div className="page dashboard-page">
      <section className="page-heading">
        <div>
          <span className="eyebrow">{t("appTagline")}</span>
          <h1>
            {t("welcome")}, {session?.displayName}
          </h1>
        </div>
      </section>
      <SummaryCards summary={summary} />
      {isDemoMode && (
        <section className="demo-reminder" aria-label={t("demoWhatsAppTitle")}>
          <div className="demo-reminder-icon" aria-hidden="true">
            ✓
          </div>
          <div>
            <span className="eyebrow">{t("demoWhatsAppTitle")}</span>
            <h2>{t("demoWhatsAppGroup")}</h2>
            <p>{t("demoWhatsAppMessage")}</p>
            <small>{t("demoWhatsAppStatus")}</small>
          </div>
        </section>
      )}
      <section className="section-heading">
        <h2>{t("overdue")}</h2>
        <Link to="/opportunities">{t("all")} →</Link>
      </section>
      <div className="opportunity-grid">
        {opportunities
          .filter((item) => item.isOverdue)
          .map((item) => (
            <OpportunityCard
              key={item.id}
              opportunity={item}
              onUpdate={() => setSelected(item)}
              onHistory={
                session?.role === "bai_admin"
                  ? () => setHistorySelected(item)
                  : undefined
              }
            />
          ))}
        {!opportunities.some((item) => item.isOverdue) && (
          <div className="empty-state">✓ {t("noResults")}</div>
        )}
      </div>
      {session?.role === "bai_admin" && (
        <section className="admin-callout">
          <div>
            <span className="eyebrow">{t("roleAdmin")}</span>
            <h2>{t("admin")}</h2>
            <p>{t("adminIntro")}</p>
          </div>
          <Link className="secondary-button" to="/admin">
            {t("admin")}
          </Link>
        </section>
      )}
      <QuickUpdateDialog
        opportunity={selected}
        onClose={() => setSelected(null)}
        onSave={save}
      />
      {session?.role === "bai_admin" ? (
        <OpportunityHistoryDialog
          opportunity={historySelected}
          onClose={() => setHistorySelected(null)}
        />
      ) : null}
      {toast && (
        <div className="toast" role="status">
          ✓ {toast}
        </div>
      )}
    </div>
  );
}
